/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { AuthenticationService } from "../../../../../core/authentication/authentication.service";
import { QueryParams } from "../../../../../core/http/query-params.type";
import { LoadingService } from "../../../../../core/loader/loading.service";
import { DynamicFormComponent } from "../../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../../shared/types/user-info";
import { CreateTicketService } from "../../create-ticket/create-ticket.service";
import { TicketService } from "../../ticket.service";
import { AddComment, SortComment, TicketComment } from "../../ticket.type";

@Component({
  selector: "app-ticket-comments",
  templateUrl: "./ticket-comments.component.html",
  styleUrls: ["./ticket-comments.component.scss"],
})
export class TicketCommentsComponent implements OnInit, OnChanges {
  @Input() status: string;
  @ViewChild(DynamicFormComponent) form: DynamicFormComponent;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() fieldEvent = new EventEmitter<string>();
  @Output() paginateChange = new EventEmitter();
  pageEvent: PageEvent;
  totalDocs: number;
  pageSize: number;
  ticketId: string;
  comments: TicketComment[];
  meta: any;
  questions: QuestionBase<string | boolean>[];
  loading = false;
  currentUser: UserInfo;
  sort: SortComment = { icon: "north", value: "asc", label: "Asc" };
  constructor(
    private ticketService: TicketService,
    private questionService: CreateTicketService,
    private route: ActivatedRoute,
    private loader: LoadingService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.loader.show();
    this.loading = true;
    this.getTicketComments();
    this.buildForm();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    if (this.meta) {
      this.totalDocs = this.meta.totalItems;
      this.pageSize = this.meta.itemsPerPage;
    }
  }

  buildForm(): void {
    this.questionService.generateCommentQuestions(this.currentUser).subscribe((qs) => {
      this.questions = qs;
    });
  }

  onSubmit(event: AddComment): void {
    this.loader.show();

    of(event.attachments?.length > 0)
      .pipe(
        mergeMap((hasAttachments) => {
          if (hasAttachments) {
            this.notificationService.info("Please hang on while we upload your files. Do not close the window.");
            const files = (event.attachments as File[]).map((file) => this.ticketService.uploadFile(file));
            return combineLatest(files).pipe(map((uploads) => uploads.map((x) => x.upload.token)));
          }

          return of([] as string[]);
        }),
      )
      .subscribe(
        (uploads) => {
          this.ticketService
            .addComment(this.ticketId, {
              comment: event.comment,
              attachments: uploads,
              ...(this.currentUser.isPartner() && { isPublicInternal: !event.isPublicInternal }),
            })
            .subscribe(
              (res) => {
                this.form.resetForm();
                this.getTicketComments();
              },
              (error) => {
                this.notificationService.error(error || "Could not add this comment to ticket");
                this.loader.hide();
              },
            );
        },
        (error) => {
          this.notificationService.error(error || "Could not add this comment to ticket");
          this.loader.hide();
        },
      );
  }

  showAddComment(): boolean {
    return this.status !== "closed";
  }

  onPaginateChange(event: PageEvent): void {
    this.loading = true;
    this.getTicketComments({ pagination: { page: event.pageIndex, pageSize: event.pageSize } });
  }

  fieldSelection(value: string): void {
    this.fieldEvent.emit(value);
  }

  toggleSort(): void {
    this.sort =
      this.sort.value === "asc"
        ? { icon: "south", value: "desc", label: "Desc" }
        : { icon: "north", value: "asc", label: "Asc" };
    this.getTicketComments();
  }

  private getTicketComments(options?: QueryParams<TicketComment>): void {
    this.ticketId = this.route.snapshot.paramMap.get("id");
    const params: QueryParams<TicketComment> = {
      ...options,
      order: { "extraInfo.createdAt": this.sort.value },
      relations: { commentAttachments: true, author: true },
    };
    if (this.ticketId) {
      this.ticketService
        .getTicketComments(this.ticketId, params)
        .subscribe((resp) => {
          this.comments = resp.items;
          this.meta = resp.meta;
          this.loading = false;
        })
        .add(() => {
          this.loader.hide();
        });
    }
  }
}
